import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Card } from 'src/Administration/Common';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import allFieldMapsConfigQuery from 'src/api/admin/allFieldMapsConfigQuery';
import createFieldMapsMutation from 'src/api/admin/createFieldMapsMutation';
import deleteFieldMapsMutation from 'src/api/admin/deleteFieldMapsMutation';
import fieldMapsConfigQuery from 'src/api/admin/fieldMapsConfigQuery';
import updateFieldMapsConfigMutation from 'src/api/admin/updateFieldMapsConfigMutation';
import updateFieldMapsMutation from 'src/api/admin/updateFieldMapsMutation';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';
import * as Yup from 'yup';
import type {
  AllFieldDefsData,
  AllFieldMapConfigsData,
  FieldDefinition,
  FieldMapConfigQueryData,
  FieldMapOption,
} from '../../types';
import styles from './EditFieldMapsPageStyles';

interface EditFieldMapsFormValues {
  name: string;
}

const EditFieldMapsPage: React.FC = () => {
  const { toastr } = useCustomToast();
  const GRID_CONTAINER_SPACING = 4;
  const classes = styles();
  const navigate = useNavigate();
  const viewState = useReactiveVar(viewStateVar);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const { data: allFieldMapsData } = useQuery<AllFieldMapConfigsData>(allFieldMapsConfigQuery, {
    fetchPolicy: 'network-only',
  });

  const selectedFieldMapsData = allFieldMapsData?.allFieldMapConfigs?.edges?.filter(
    (f) => f.node.name === viewState.editFieldMapsName,
  );

  const { data: fieldMapsConfigInfo, loading } = useQuery<FieldMapConfigQueryData>(fieldMapsConfigQuery, {
    fetchPolicy: 'network-only',
    variables: {
      name: viewState.editFieldMapsName,
    },
    onCompleted: (data) => {
      const mappingData = data?.fieldMapConfig?.mapping || [];
      setFieldOptions(JSON.parse(JSON.stringify(mappingData)));
    },
  });

  useEffect(() => {
    if (!loading && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [loading]);

  const fieldMapsConfigMappingInfo = fieldMapsConfigInfo?.fieldMapConfig?.mapping;

  const initialFieldMapsConfigInfo = {
    fieldName: fieldMapsConfigInfo?.fieldMapConfig?.name || '',
    fieldOptions: fieldMapsConfigMappingInfo || [],
  };

  const [fieldOptions, setFieldOptions] = useState<FieldMapOption[]>(() => {
    return initialFieldMapsConfigInfo?.fieldOptions
      ? JSON.parse(JSON.stringify(initialFieldMapsConfigInfo.fieldOptions))
      : [];
  });

  const { data: allAvailableFieldDefs } = useQuery<AllFieldDefsData>(allFieldDefinitionsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const allAvailableFieldDefsNode: FieldDefinition[] | undefined = allAvailableFieldDefs?.allFieldDefs?.edges?.map(
    (item) => item.node,
  );

  const [createFieldMaps] = useMutation(createFieldMapsMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [allFieldMapsConfigQuery],
  });

  const [updateFieldMapsConfig] = useMutation(updateFieldMapsConfigMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [fieldMapsConfigQuery],
  });

  const [updateFieldMaps] = useMutation(updateFieldMapsMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [fieldMapsConfigQuery],
  });

  const [deleteFieldMaps] = useMutation(deleteFieldMapsMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [fieldMapsConfigQuery],
  });

  const onClickCancel = (): void => {
    navigate({ to: '/systemadmin/metadata/field-maps' });
  };

  const addOptionHandler = (): void => {
    const emptyField: FieldMapOption = { externalFieldId: '', fieldDefId: '' };
    setFieldOptions((currentOptions) => {
      const safeOptions = Array.isArray(currentOptions) ? currentOptions : [];
      return [...safeOptions, emptyField];
    });
  };

  const removeOptionHandler = (index: number): void => {
    const selectedExternalFieldId = fieldOptions[index]?.externalFieldId;

    let selectedFieldMapId: string | undefined;
    if (selectedExternalFieldId && selectedFieldMapsData?.[0]) {
      const selectedFieldMapItem = selectedFieldMapsData[0]?.node?.fieldmapSet?.edges?.filter(
        (f) => f.node.externalFieldId === selectedExternalFieldId,
      );
      selectedFieldMapId = selectedFieldMapItem?.[0]?.node?.id;
    }

    setFieldOptions((prevState) => {
      const updatedPairs: FieldMapOption[] = JSON.parse(JSON.stringify(prevState));
      updatedPairs.splice(index, 1);
      return updatedPairs;
    });

    if (selectedFieldMapId) {
      const currentSelectedForDelete = viewStateVar().selectedFieldMapsForDelete || [];
      viewStateVar({
        ...viewStateVar(),
        selectedFieldMapsForDelete: [...currentSelectedForDelete, selectedFieldMapId],
      });
    }
  };

  const updateFieldOptions = (index: number, keyOrValue: keyof FieldMapOption, newValue: string): void => {
    setFieldOptions((prevState) => {
      const updatedPairs: FieldMapOption[] = JSON.parse(JSON.stringify(prevState));
      updatedPairs[index][keyOrValue] = newValue;
      return updatedPairs;
    });
  };

  const formik = useFormik<EditFieldMapsFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: initialFieldMapsConfigInfo.fieldName,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please provide a field name'),
    }),
    onSubmit: async (values, submitProps: FormikHelpers<EditFieldMapsFormValues>) => {
      const { name } = values;

      try {
        // Step 1: Update the field map config name
        if (selectedFieldMapsData?.[0]) {
          await updateFieldMapsConfig({
            variables: {
              fieldMapConfigId: selectedFieldMapsData[0]?.node?.id,
              name,
            },
          });
        }

        // Step 2: Handle deletions first to avoid conflicts
        if (viewState.selectedFieldMapsForDelete && viewState.selectedFieldMapsForDelete.length > 1) {
          const deletePromises: Promise<unknown>[] = [];
          for (const item of viewState.selectedFieldMapsForDelete) {
            if (item) {
              deletePromises.push(
                deleteFieldMaps({
                  variables: {
                    fieldMapId: item,
                  },
                }).catch((error: Error) => {
                  console.warn('Error deleting field map:', error);
                }),
              );
            }
          }
          await Promise.all(deletePromises);
        }

        // Step 3: Create new field maps with error handling
        const createPromises: Promise<unknown>[] = [];
        for (const { externalFieldId, fieldDefId } of fieldOptions) {
          if (externalFieldId && fieldDefId && selectedFieldMapsData?.[0]) {
            createPromises.push(
              createFieldMaps({
                variables: {
                  fieldMapConfigId: selectedFieldMapsData[0]?.node?.id,
                  externalFieldId: externalFieldId,
                  fieldDefId: fieldDefId,
                },
              }).catch((error: Error) => {
                if (!error.message.includes('already exists')) {
                  console.warn('Error creating field map:', error);
                }
              }),
            );
          }
        }
        await Promise.all(createPromises);

        // Step 4: Update existing field maps
        if (selectedFieldMapsData?.[0]?.node?.fieldmapSet?.edges) {
          const fieldMapsFieldmapSet = selectedFieldMapsData[0].node.fieldmapSet.edges.map((item) => item.node);
          const updatePromises: Promise<unknown>[] = [];
          for (const { id, externalFieldId, fieldDefId } of fieldMapsFieldmapSet) {
            if (id && externalFieldId && fieldDefId) {
              updatePromises.push(
                updateFieldMaps({
                  variables: {
                    fieldMapId: id,
                    externalFieldId,
                    fieldDefId,
                  },
                }).catch((error: Error) => {
                  console.warn('Error updating field map:', error);
                }),
              );
            }
          }
          await Promise.all(updatePromises);
        }

        toastr.success(`Field Map was edited successfully`);
        submitProps.resetForm({ values });
        navigate({ to: '/systemadmin/metadata/field-maps' });
      } catch (e) {
        console.error('Field map update error:', e);
        toastr.error((e as Error).message || 'An error occurred while saving field maps');
      }
    },
  });

  if (!fieldMapsConfigInfo) return null;

  if (loading) return <CircularProgress color="secondary" />;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card className={classes.mainCard}>
        <Grid className={classes.mainGrid} container spacing={GRID_CONTAINER_SPACING}>
          <div className={classes.textWrapper}>
            <Typography className={classes.newFieldText}>Edit Field</Typography>
            {formik.errors.name && <Typography className={classes.fieldErrors}>{formik.errors.name}</Typography>}
          </div>
          <Grid style={{ justifyContent: 'flex-end' }} className={classes.displayName} item xs={12}>
            <Typography className={classes.displayNameText}>Display Name</Typography>
            <InputBase {...formik.getFieldProps('name')} className={classes.displayNameInput} inputRef={nameInputRef} />
            <div className={classes.externalDefText}>
              <Typography className={classes.externalFieldIdText}>External Field ID</Typography>
              <Typography className={classes.fieldDefinitionText}>Field Definition</Typography>
            </div>
          </Grid>

          <Grid className={classes.choicesSection} item xs={12}>
            {fieldOptions?.map((pair, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: field options have no stable identifier until saved
              <div className={classes.choicesInputs} key={`field-option-${index}`}>
                <InputBase
                  value={pair.externalFieldId}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    updateFieldOptions(index, 'externalFieldId', e.target.value)
                  }
                  className={classes.optionInput}
                />
                <FormControl className={classes.fieldTypeSelect}>
                  <Select
                    value={pair.fieldDefId}
                    onChange={(e: SelectChangeEvent<string>) => updateFieldOptions(index, 'fieldDefId', e.target.value)}
                    placeholder="Select Role"
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Select Role"
                  >
                    {allAvailableFieldDefsNode?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton onClick={() => removeOptionHandler(index)} size="large">
                  <CloseIcon />
                </IconButton>
              </div>
            ))}

            <div className={classes.addOptionButton}>
              <Button onClick={addOptionHandler} variant="contained" color="primary">
                Add
              </Button>
            </div>
          </Grid>

          <Grid className={classes.addCancelButtons} item xs={12}>
            <Button disabled={Object.keys(formik.errors).length > 0} variant="contained" color="primary" type="submit">
              Save
            </Button>
            <Button
              color="secondary"
              style={{
                marginLeft: '10px',
                marginRight: '10px',
              }}
              onClick={onClickCancel}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Card>
    </form>
  );
};

export default EditFieldMapsPage;
